/**
 * Helpers for bidirectional linking between the Argus chat and entity
 * detail pages.
 *
 * When the frontend acts on a `navigate` tool result (either auto-navigate
 * or the user clicks the card), we append `argus_session_id` and
 * `argus_instance_id` query params to the target URL so the destination
 * page can render the "Created by Argus" badge without any backend work.
 *
 * Detail pages consume `useArgusParams()` to read those params and decide
 * whether to show the badge.
 *
 * We deliberately do *not* stamp URLs inside the pi tool extension — the
 * extension's ExtensionContext does not expose the current session ID,
 * and punting the work to the frontend keeps the tool schema simple.
 */

export const ARGUS_SESSION_PARAM = "argus_session_id";
export const ARGUS_INSTANCE_PARAM = "argus_instance_id";

export interface ArgusParams {
  sessionId: string | null;
  instanceId: string | null;
}

/**
 * Append the given session + instance IDs to a path that may or may not
 * already contain a query string. Existing query params are preserved;
 * the Argus tracking params overwrite any prior values under the same key.
 */
export function appendArgusParams(
  path: string,
  params: { sessionId?: string | null; instanceId?: string | null },
): string {
  if (!params.sessionId && !params.instanceId) return path;
  const [base, existingQuery] = path.split("?");
  const search = new URLSearchParams(existingQuery ?? "");
  if (params.sessionId) search.set(ARGUS_SESSION_PARAM, params.sessionId);
  if (params.instanceId) search.set(ARGUS_INSTANCE_PARAM, params.instanceId);
  const qs = search.toString();
  return qs ? `${base}?${qs}` : (base ?? path);
}

export function readArgusParams(searchParams: URLSearchParams): ArgusParams {
  return {
    sessionId: searchParams.get(ARGUS_SESSION_PARAM),
    instanceId: searchParams.get(ARGUS_INSTANCE_PARAM),
  };
}
